import { BaseApiRequest, BasePutApiRequest } from '@api/Trinium/types';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { SalesPerson } from '../Salepeople/types';
import { createSelector } from '@reduxjs/toolkit';

interface CustomerRawFields {
  id: string;
  name: string;
  addr1?: string;
  addr2?: string;
  city?: string;
  state?: string;
  zip?: string;
  emailAddr?: string;
  phone?: string;
  fax?: string;
  availCredit?: number;
  creditLmt?: number;
  creditSts?: string;
  hasCreditWrn?: boolean;
  slsId?: string;
  sls?: {
    name: string;
  };
  isActive?: boolean;
}

export type Customer = GridValidRowModel &
  Omit<CustomerRawFields, 'slsId' | 'sls'> & {
    salesperson?: SalesPerson | null;
  };

export function transformCustomerResponse(result: CustomerRawFields[] | undefined): Customer[] {
  return result
    ? sortCustomers(
        result.map(
          ({ slsId, sls, isActive, hasCreditWrn, ...persistedCustomerProps }) =>
            ({
              isActive: isActive ?? false,
              hasCreditWrn: hasCreditWrn ?? false,
              salesperson: slsId ? { id: slsId, name: sls?.name } : null,
              ...persistedCustomerProps
            }) as Customer
        )
      )
    : [];
}

/** Method for sorting Customers */
export function sortCustomers(customers: Customer[]) {
  return customers.sort((a, b) => {
    // sort by Customer Name
    return a.name.localeCompare(b.name, 'en', {
      sensitivity: 'base'
    });
  });
}

type RequestCreditIncreasePutRequest = BasePutApiRequest<'requestCreditIncrease', { subject: string; body: string; reqdLmt: number }> & {
  id: string;
};

export type PutCustomerRequest = RequestCreditIncreasePutRequest;

export interface FetchCustomerRequest extends BaseApiRequest {
  customerId: string;
  role?: string;
}

export type PostCustomerContactRequest = Omit<FetchCustomerRequest, 'role'> & Omit<CustomerContact, 'id' | 'city'> & { cityId?: string };

export interface CustomerContact {
  id: number;
  typeId: string;
  type: string;
  isMain?: boolean;
  name: string;
  phone: string;
  email: string;
  role?: string;
  site: string;
  cityId?: string;
  city?: string;
  state?: string;
}

export interface PostCustomerContactResponse {
  /** The new contact that was posted */
  newContact: CustomerContact;
  /** The new list including the contact that was posted */
  contacts: CustomerContact[];
}

/**
 * Transforming the API response so that we can track which record was just added to default into inputs
 */
export function transformPostCustomerContact(response: CustomerContact[], _meta: object | undefined, arg: PostCustomerContactRequest) {
  const newContact = response.find((contact) => contact.name === arg.name);

  return {
    newContact: newContact ?? { id: 0, ...arg },
    contacts: response
  };
}

/** RTK Selector for filtering the work order contacts */
export const selectWorkOrderContacts = createSelector(
  (contacts: CustomerContact[] | null | undefined) => contacts,
  (contacts) => {
    if (!contacts) return [];

    return contacts
      .filter((c) => c.role?.toLowerCase() === 'wo' || c.role?.toLowerCase() === 'wono')
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'en', {
          sensitivity: 'base'
        })
      );
  }
);
